// AvatarCircle — initials fallback avatar.
//
// 24px default per Pencil's "LH / AE / SM" mocks. Pale Sage circle bg,
// Stone-deep initials text. The `avatarBg` and `avatarText` color tokens
// share their hex with `sageTint` / `textSecondary` respectively, but we
// import the named tokens here to make the intent legible — this is *the*
// avatar surface, not a generic Sage tint.
//
// If imageUrl is provided, render an <img>; otherwise compute two-letter
// initials from `initials` or `name` (first letter of first two
// whitespace-split words; falls back to "?").

import { forwardRef, type HTMLAttributes } from "react";

import { colors } from "@/design-system/tokens";

import { cn } from "./cn";

export type AvatarSize = "sm" | "md" | "lg";

export interface AvatarCircleProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  size?: AvatarSize;
  /** Pre-computed initials (1–2 chars). Takes precedence over `name`. */
  initials?: string;
  /** Person's display name; first 2 word-initials are used. */
  name?: string;
  /** Optional image URL. Renders an <img> instead of initials. */
  imageUrl?: string;
  /** Accessible label override. Defaults to `name` or `initials`. */
  alt?: string;
}

const SIZE_PX: Record<AvatarSize, number> = { sm: 20, md: 24, lg: 32 };
const SIZE_TEXT: Record<AvatarSize, string> = {
  sm: "text-xs",
  md: "text-xs",
  lg: "text-sm",
};

function deriveInitials(name?: string, initials?: string): string {
  if (initials && initials.trim().length > 0) {
    return initials.slice(0, 2).toUpperCase();
  }
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export const AvatarCircle = forwardRef<HTMLSpanElement, AvatarCircleProps>(
  function AvatarCircle(
    { size = "md", initials, name, imageUrl, alt, className, style, ...rest },
    ref,
  ) {
    const px = SIZE_PX[size];
    const accessibleName = alt ?? name ?? initials ?? "Avatar";
    const computedInitials = deriveInitials(name, initials);

    return (
      <span
        ref={ref}
        role="img"
        aria-label={accessibleName}
        data-size={size}
        className={cn(
          "inline-flex items-center justify-center rounded-full overflow-hidden",
          "font-medium",
          SIZE_TEXT[size],
          className,
        )}
        style={{
          width: px,
          height: px,
          backgroundColor: colors.avatarBg,
          color: colors.avatarText,
          ...style,
        }}
        {...rest}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span aria-hidden="true">{computedInitials}</span>
        )}
      </span>
    );
  },
);
